/** Responsibility: Generate and cache per-world taxonomies (item categories, NPC archetypes, combat archetypes) via AI.
 *  Scope: Engine-only; consumes AI router for generation, never touches UI. */
import { WorldColorScheme, type WorldTaxonomy } from "@anyquest/shared-core"
import { router } from '../ai/router'

const TAXONOMY_SYSTEM = `You are a world-building visual architect and taxonomist. Given a world seed, produce a JSON object with exactly these keys:
- "worldName": A unique, evocative name for this reality.
- "coreReality": 1-2 sentences describing the core conflict, physics, or vibe of this world.
- "itemCategories": 10-15 objects { "pattern": "regex", "category": "snake_case" }
- "visualNpcArchetypes": 6-8 objects { "pattern": "regex", "key": "snake_case", "label": "archetype" }
- "combatNpcArchetypes": 6-8 objects { "pattern": "regex", "archetype": "SCREAMING", "style": "prose" }
- "imageStyle": art direction string.
- "colorScheme": A complete object matching the WorldColorScheme class. 
- "economy": { "currencyName": string, "currencySymbol": string, "rationCost": number }

ECONOMY RULES:
1. "currencyName": A name that fits the world seed (e.g. "Credits", "Soul Shards", "Copper Bits").
2. "currencySymbol": A 1-2 character symbol or abbreviation.
3. "rationCost": The cost of 1 standard day of food/water for one person. This is the basis for all item valuation. Usually 5-20 units.

COLOR SCHEME RULES:
1. All values must be valid HEX or RGBA strings.
2. ACCESSIBILITY: "--text-prose" MUST have a contrast ratio of at least 4.5:1 against "--bg".
3. ACCESSIBILITY: "--gold" (accents) MUST have a contrast ratio of at least 3:1 against "--bg".
4. THEME: Choose colors that evoke the world seed (e.g. deep neons for Cyberpunk, dusty ochres for Western).
5. TOKENS: Provide values for:
   - Surfaces: --bg, --surface, --surface-raised, --surface-card-1/2/3, --border, --border-subtle
   - Text: --text-prose, --text-muted, --text-dim, --text-sys
   - Accents: --brand-primary, --brand-primary-glow, --brand-secondary, --gold, --gold-glow
   - Semantic: --sem-success-fg/bg/border, --sem-warn-fg/bg/border, --sem-danger-fg/bg/border
   - Chips: --chip-ally-fg/border/surface-a/b, --chip-npc-fg/border/surface-a/b, --chip-item-fg/border/surface-a/b
   - Gradients: --grad-main-bg, --grad-overlay-fade, --grad-surface-shine (Full CSS gradient strings)

Return ONLY valid JSON.`

function buildTaxonomyPrompt(worldSeed: string): string {
  return `World seed: "${worldSeed}"\n\nGenerate the full taxonomy JSON for this world.`
}

const FALLBACK_TAXONOMY: WorldTaxonomy = {
  worldName: 'The Nameless Reach',
  coreReality: 'A fragmented reality of floating islands and ancient machinery.',
  itemCategories: [
    { pattern: 'sword|blade|dagger|axe|spear|lance|rapier|katana', category: 'weapon_blade' },
    { pattern: 'bow|crossbow|arrow|quiver', category: 'weapon_ranged' },
    { pattern: 'staff|wand|rod|orb|tome|grimoire', category: 'magical_implement' },
    { pattern: 'potion|elixir|vial|brew|tincture', category: 'potion' },
    { pattern: 'scroll|parchment|manuscript|letter', category: 'scroll' },
    { pattern: 'shield|buckler|aegis', category: 'shield' },
    { pattern: 'armor|mail|plate|robe|cloak', category: 'armor' },
    { pattern: 'ring|amulet|pendant|necklace|bracelet', category: 'jewelry' },
    { pattern: 'coin|gold|silver|treasure|gem|jewel', category: 'treasure' },
    { pattern: 'key|lockpick', category: 'key' },
    { pattern: 'food|bread|meat|fruit|ration', category: 'food' },
    { pattern: 'torch|lantern|candle', category: 'light_source' },
    { pattern: 'map|compass', category: 'navigation_tool' },
  ],
  visualNpcArchetypes: [
    { pattern: 'guard|sentinel|watchman|watcher|warden', key: 'guard', label: 'city guard' },
    { pattern: 'merchant|trader|vendor|shopkeeper|peddler', key: 'merchant', label: 'traveling merchant' },
    { pattern: 'villager|commoner|townsfolk|peasant', key: 'villager', label: 'village commoner' },
    { pattern: 'bandit|raider|thug|brigand|outlaw', key: 'bandit', label: 'bandit' },
    { pattern: 'soldier|mercenary|knight|man-?at-?arms', key: 'soldier', label: 'frontline soldier' },
    { pattern: 'priest|cleric|acolyte|monk', key: 'cleric', label: 'temple cleric' },
    { pattern: 'scholar|sage|scribe|librarian', key: 'scholar', label: 'arcane scholar' },
  ],
  combatNpcArchetypes: [
    { pattern: 'vampire|wraith|lich|specter|shade|leech|shadow', archetype: 'DRAIN', style: 'Life-draining attacks that sap vitality and spirit.' },
    { pattern: 'dragon|wyrm|drake|wyvern', archetype: 'DRAGON', style: 'Devastating breath and claw attacks with massive damage potential.' },
    { pattern: 'wizard|mage|sorcerer|warlock|necromancer', archetype: 'CASTER', style: 'Ranged magical attacks targeting spirit and applying debuffs.' },
    { pattern: 'assassin|rogue|thief|ninja|spy', archetype: 'ASSASSIN', style: 'Fast precision strikes that exploit weak points and steal items.' },
    { pattern: 'elemental|golem|djinn|ifrit|sprite', archetype: 'ELEMENTAL', style: 'Area elemental damage with environmental effects.' },
    { pattern: 'diplomat|noble|bard|siren|enchant', archetype: 'SOCIAL', style: 'Manipulation and charm that drains spirit and applies confusion.' },
  ],
  imageStyle: 'dark fantasy oil painting with cinematic lighting',
  colorScheme: new WorldColorScheme(),
  economy: { currencyName: 'Gold Pieces', currencySymbol: 'GP', rationCost: 10 },
}

function sanitizeCurrencyName(value: unknown): string {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ')
  return text || FALLBACK_TAXONOMY.economy.currencyName
}

function sanitizeCurrencySymbol(value: unknown): string {
  const raw = String(value ?? '').trim()
  const filtered = Array.from(raw)
    .filter(char => /[\p{Sc}A-Za-z0-9+\-.]/u.test(char))
    .join('')
    .slice(0, 3)
  if (filtered) return filtered
  const fallback = String(raw).replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase()
  return fallback || FALLBACK_TAXONOMY.economy.currencySymbol
}

function sanitizeRationCost(value: unknown): number {
  const num = Number(value)
  if (!Number.isFinite(num)) return FALLBACK_TAXONOMY.economy.rationCost
  return Math.max(1, Math.min(9999, Math.round(num)))
}

function validateTaxonomy(raw: unknown): WorldTaxonomy | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  
  if (typeof r.worldName !== 'string' || !r.worldName) return null
  if (typeof r.coreReality !== 'string' || !r.coreReality) return null
  if (!Array.isArray(r.itemCategories) || r.itemCategories.length < 5) return null
  if (!Array.isArray(r.visualNpcArchetypes) || r.visualNpcArchetypes.length < 3) return null
  if (!Array.isArray(r.combatNpcArchetypes) || r.combatNpcArchetypes.length < 3) return null
  if (typeof r.imageStyle !== 'string' || !r.imageStyle) return null
  
  const rawEconomy = (r.economy && typeof r.economy === 'object') ? r.economy as Record<string, unknown> : FALLBACK_TAXONOMY.economy
  if (typeof rawEconomy.currencyName !== 'string') return null
  if (typeof rawEconomy.rationCost !== 'number') return null
  const economy = {
    currencyName: sanitizeCurrencyName(rawEconomy.currencyName),
    currencySymbol: sanitizeCurrencySymbol(rawEconomy.currencySymbol),
    rationCost: sanitizeRationCost(rawEconomy.rationCost),
  }

  // Validate regex patterns compile
  try {
    for (const c of r.itemCategories) new RegExp((c as { pattern: string }).pattern, 'i')
    for (const a of r.visualNpcArchetypes) new RegExp((a as { pattern: string }).pattern, 'i')
    for (const a of r.combatNpcArchetypes) new RegExp((a as { pattern: string }).pattern, 'i')
  } catch { return null; }
  
  return {
    worldName: r.worldName,
    coreReality: r.coreReality,
    itemCategories: r.itemCategories as any,
    visualNpcArchetypes: r.visualNpcArchetypes as any,
    combatNpcArchetypes: r.combatNpcArchetypes as any,
    imageStyle: r.imageStyle,
    colorScheme: WorldColorScheme.fromAI(r.colorScheme),
    economy,
  }
}

export async function generateWorldTaxonomy(worldSeed: string): Promise<WorldTaxonomy> {
  try {
    const result = await router.route<WorldTaxonomy>({
      jobType: 'genesis',
      systemPrompt: TAXONOMY_SYSTEM,
      userPrompt: buildTaxonomyPrompt(worldSeed),
    })
    return validateTaxonomy(result) ?? FALLBACK_TAXONOMY
  } catch (err) {
    console.warn('[WorldTaxonomy] AI generation failed, using fallback:', err)
    return FALLBACK_TAXONOMY
  }
}

export function getTaxonomyItemCategory(taxonomy: WorldTaxonomy, itemName: string): string | null {
  const lower = itemName.toLowerCase()
  for (const { pattern, category } of taxonomy.itemCategories) {
    // Harden pattern with word boundaries to prevent substring collisions
    const anchored = `\\b(${pattern})\\b`
    try { if (new RegExp(anchored, 'i').test(lower)) return category; } catch { continue; }
  }
  return null
}

export function getTaxonomyNpcVisual(taxonomy: WorldTaxonomy, identity: string): { key: string; label: string } | null {
  for (const { pattern, key, label } of taxonomy.visualNpcArchetypes) {
    const anchored = `\\b(${pattern})\\b`
    try { if (new RegExp(anchored, 'i').test(identity)) return { key, label }; } catch { continue; }
  }
  return null
}

export function getTaxonomyCombatArchetype(taxonomy: WorldTaxonomy, identity: string): { archetype: string; style: string } | null {
  for (const { pattern, archetype, style } of taxonomy.combatNpcArchetypes) {
    const anchored = `\\b(${pattern})\\b`
    try { if (new RegExp(anchored, 'i').test(identity)) return { archetype, style }; } catch { continue; }
  }
  return null
}

export { FALLBACK_TAXONOMY }
